import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Visibility,
  Download,
  BarChart,
  School,
  Person
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';

const Notes = () => {
  const { isAdmin, isEnseignant, isEtudiant, isChefFiliere, isDirectionGenerale, isCoordinateurPedagogique, isResponsableScolarite, user } = usePermissions();
  // La scolarite ne saisi pas : la direction, les enseignants et le chef de
  // filiere gerent les notes (saisie, controles, statistiques).
  const peutSaisirNotes = isAdmin || isEnseignant || isChefFiliere || isResponsableScolarite;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // États pour les données
  const [matieres, setMatieres] = useState([]);
  const [controles, setControles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [selectedMatiere, setSelectedMatiere] = useState('');
  const [selectedEtudiant, setSelectedEtudiant] = useState('');
  const [bulletin, setBulletin] = useState(null);
  
  // États pour les modals
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState({
    etudiant_id: '',
    controle_id: '',
    matiere_id: '',
    valeur: '',
    commentaire: ''
  });
  const [selectedNote, setSelectedNote] = useState(null);
  
  // États pour les statistiques
  const [stats, setStats] = useState(null);

  // États pour la gestion des contrôles
  const [periodes, setPeriodes] = useState([]);
  const [periodesFiltre, setPeriodesFiltre] = useState([]);
  const [periodesForm, setPeriodesForm] = useState([]);
  const [controlesList, setControlesList] = useState([]);
  const [filterMatiere, setFilterMatiere] = useState('');
  const [filterPeriode, setFilterPeriode] = useState('');
  const [anneesUniv, setAnneesUniv] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [niveauxFiltre, setNiveauxFiltre] = useState([]);
  const [niveauxForm, setNiveauxForm] = useState([]);
  const [modules, setModules] = useState([]);
  const [filterAnnee, setFilterAnnee] = useState('');
  const [filterFiliere, setFilterFiliere] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [filterModule, setFilterModule] = useState('');

  // États pour la saisie des notes
  const [saisieAnnee, setSaisieAnnee] = useState('');
  const [saisieFiliere, setSaisieFiliere] = useState('');
  const [saisieNiveau, setSaisieNiveau] = useState('');
  const [saisiePeriode, setSaisiePeriode] = useState('');
  const [saisieModule, setSaisieModule] = useState('');
  const [saisieMatiere, setSaisieMatiere] = useState('');
  const [saisieControle, setSaisieControle] = useState('');
  const [saisieNiveaux, setSaisieNiveaux] = useState([]);
  const [saisiePeriodes, setSaisiePeriodes] = useState([]);
  const [saisieControles, setSaisieControles] = useState([]);
  const [saisieEtudiants, setSaisieEtudiants] = useState([]);
  const [saisieNotesMap, setSaisieNotesMap] = useState({}); // etudiant_id -> {valeur, noteId, dirty, commentaire}
  const [saisieLoading, setSaisieLoading] = useState(false);
  const [saisieSaving, setSaisieSaving] = useState(false);
  const [controleFormAnnee, setControleFormAnnee] = useState('');
  const [controleFormFiliere, setControleFormFiliere] = useState('');
  const [controleFormNiveau, setControleFormNiveau] = useState('');
  const [controleFormModule, setControleFormModule] = useState('');
  const [modalControleOpen, setModalControleOpen] = useState(false);
  const [controleMode, setControleMode] = useState('add');
  const [selectedControle, setSelectedControle] = useState(null);
  const [controleForm, setControleForm] = useState({
    intitule: '',
    type: 'CONTROLE_CONTINU',
    matiere_id: '',
    periode_id: '',
    annee_universitaire_id: '',
    coefficient: 1,
    session: 'NORMALE',
    controle_type: 0,
    date: '',
    controle_h_debut: '',
    controle_h_fin: '',
    salle_id: '',
    estActif: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const memeId = (a, b) =>
    a !== undefined && a !== null &&
    b !== undefined && b !== null &&
    String(a) === String(b);
  useEffect(() => {
    loadData();
    // also load modules/matieres globally in case
    const headers = getAuthHeaders();
    axios.get('/api/modules', { headers }).then(r => { if (r.data?.length) setModules(r.data); }).catch(()=>{});
    axios.get('/api/matieres', { headers }).then(r => { if (r.data?.length) setMatieres(r.data); }).catch(()=>{});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      // Charger les matières (filtrées par enseignant si rôle ENSEIGNANT)
      const matieresUrl = isEnseignant && userData.enseignant_id
        ? `/api/matieres/enseignant/${userData.enseignant_id}`
        : '/api/matieres';
      const matieresRes = await axios.get(matieresUrl, { headers });
console.log('[loadData] matieres brutes:', matieresRes.data?.length, matieresRes.data?.[0]);
const matieresUniques = Array.from(
  new Map((matieresRes.data || []).map(m => [String(m._id), m])).values()
);
setMatieres(matieresUniques);
      try {
        const periodesRes = await axios.get('/api/periodes', { headers });
        setPeriodes(periodesRes.data || []);
      } catch (err) {
        console.error('Erreur chargement périodes:', err);
      }
      try {
  const anneesRes = await axios.get('/api/annees', { headers });
  setAnneesUniv(anneesRes.data || []);
} catch (err) {
  console.error('Erreur chargement années:', err);
}
      try {
        const filieresRes = await axios.get('/api/filieres', { headers });
        setFilieres(filieresRes.data || []);
      } catch (err) {
        console.error('Erreur chargement filières:', err);
      }
      try {
        const niveauxRes = await axios.get('/api/niveaux', { headers });
        setNiveaux(niveauxRes.data || []);
      } catch (err) {
        console.error('Erreur chargement niveaux:', err);
      }
      try {
  const modulesRes = await axios.get('/api/modules', { headers });
  console.log('[loadData] modules bruts:', modulesRes.data?.length, modulesRes.data?.[0]);
  const modulesUniques = Array.from(
  new Map((modulesRes.data || []).map(m => [String(m._id), m])).values()
);
setModules(modulesUniques);
} catch (err) {
  console.error('Erreur chargement modules:', err);
}

      let etudiantsList = [];
      if (isAdmin || isChefFiliere || isDirectionGenerale || isCoordinateurPedagogique || isResponsableScolarite) {
        const etudiantsRes = await axios.get('/api/etudiants', { headers });
        etudiantsList = etudiantsRes.data?.data || [];
      } else if (isEnseignant && userData.enseignant_id) {
        // Charger uniquement les étudiants des groupes (filières/niveaux) de l'enseignant
        const mesMatieres = matieresRes.data || [];
        const groupKeys = mesMatieres
          .filter(m => m.filiere_id && m.niveau_id)
          .map(m => `${m.filiere_id}|${m.niveau_id}`);
        const groupesUniques = [...new Set(groupKeys)];

        let inscriptions = [];
        try {
          const insRes = await axios.get('/api/inscriptions', { headers, timeout: 8000 });
          inscriptions = insRes.data?.data || insRes.data || [];
        } catch (err) {
          console.error('Erreur chargement inscriptions:', err);
        }

        const idsEtudiants = new Set();
        inscriptions.forEach(ins => {
          const filiereId = ins.filiere?._id || ins.filiere_id;
          const niveauId = ins.niveau?._id || ins.niveau_id;
          if (
            ins.statut === 'CONFIRMEE' &&
            groupesUniques.includes(`${filiereId}|${niveauId}`)
          ) {
            const etudiantId = ins.etudiant?._id || ins.etudiant_id;
            if (etudiantId) idsEtudiants.add(String(etudiantId));
          }
        });

        if (idsEtudiants.size > 0) {
          const etudiantsRes = await axios.get('/api/etudiants', { headers });
          const all = etudiantsRes.data?.data || [];
          etudiantsList = all.filter(e => idsEtudiants.has(String(e._id)));
        }
      }

      // Si étudiant, charger ses notes
      if (isEtudiant) {
        const etudiant = etudiantsList.find(e => e.etudiant_email === userData.email) ||
          (etudiantsList.length === 0 ? null : etudiantsList[0]);
        if (etudiant) {
          setSelectedEtudiant(etudiant.etudiant_id || etudiant._id);
          await loadBulletin(etudiant.etudiant_id || etudiant._id);
        }
      }

      setEtudiants(etudiantsList);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const loadBulletin = async (etudiantId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/etudiant/${etudiantId}/bulletin`, { headers });
      setBulletin(res.data);
      setSelectedEtudiant(etudiantId);
    } catch (err) {
      console.error('Erreur chargement bulletin:', err);
      setError('Impossible de charger le bulletin');
    }
  };

  const loadNotesByMatiere = async (matiereId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/matiere/${matiereId}`, { headers });
      setNotes(res.data || []);
      setSelectedMatiere(matiereId);
    } catch (err) {
      console.error('Erreur chargement notes:', err);
    }
  };

  const loadControlesByMatiere = async (matiereId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/notes/controles/matiere/${matiereId}`, { headers });
      setControles(res.data || []);
    } catch (err) {
      console.error('Erreur chargement contrôles:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && selectedMatiere) {
      loadNotesByMatiere(selectedMatiere);
    }
    if (newValue === 0 && selectedEtudiant) {
      loadBulletin(selectedEtudiant);
    }
  };

  const handleMatiereChange = (matiereId) => {
    setSelectedMatiere(matiereId);
    loadControlesByMatiere(matiereId);
    loadNotesByMatiere(matiereId);
  };

  const handleAddNote = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post('/api/notes', formData, { headers });
      setSuccessMessage('Note ajoutée avec succès');
      handleCloseModal();
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const handleUpdateNote = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`/api/notes/${selectedNote._id}`, formData, { headers });
      setSuccessMessage('Note modifiée avec succès');
      handleCloseModal();
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/notes/${noteId}`, { headers });
      setSuccessMessage('Note supprimée avec succès');
      loadNotesByMatiere(selectedMatiere);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleOpenModal = (mode, note = null) => {
    setModalMode(mode);
    if (note) {
      setSelectedNote(note);
      setFormData({
        etudiant_id: note.etudiant_id || '',
        controle_id: note.controle_id || '',
        matiere_id: note.matiere_id || '',
        valeur: note.valeur || '',
        commentaire: note.commentaire || ''
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNote(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      etudiant_id: '',
      controle_id: '',
      matiere_id: '',
      valeur: '',
      commentaire: ''
    });
  };

  const getMention = (moyenne) => {
    if (moyenne >= 18) return { label: 'Excellent', color: '#8b5cf6' };
    if (moyenne >= 16) return { label: 'Très Bien', color: '#3b82f6' };
    if (moyenne >= 14) return { label: 'Bien', color: '#10b981' };
    if (moyenne >= 12) return { label: 'Assez Bien', color: '#f59e0b' };
    if (moyenne >= 10) return { label: 'Passable', color: '#f97316' };
    return { label: 'Non validé', color: '#ef4444' };
  };

  const getTypeColor = (type) => {
    const colors = {
      'EXAMEN_FINAL': '#ef4444',
      'EXAMEN_INTERMEDIAIRE': '#f59e0b',
      'CONTROLE_CONTINU': '#10b981',
      'TP': '#3b82f6',
      'TD': '#8b5cf6',
      'PROJET': '#06b6d4',
      'STAGE': '#14b8a6',
      'RATTRAPAGE': '#f43f5e'
    };
    return colors[type] || '#6b7280';
  };

  const loadSaisieTable = async () => {
    if (!saisieControle) {
      setSaisieEtudiants([]);
      setSaisieNotesMap({});
      return;
    }
    try {
      setSaisieLoading(true);
      const headers = getAuthHeaders();
      const [ctrlRes, insRes, etuRes] = await Promise.all([
        axios.get(`/api/notes/controle/${saisieControle}`, { headers }),
        axios.get('/api/inscriptions', { headers, timeout: 10000 }),
        axios.get('/api/etudiants', { headers, timeout: 10000 }).catch(() => ({ data: [] }))
      ]);
      const controle = ctrlRes.data?.controle || {};
      const notesExist = ctrlRes.data?.notes || [];
      const inscriptions = insRes.data?.data || insRes.data || [];
      const allEtudiants = etuRes.data?.data || etuRes.data || [];
      const etuMap = new Map();
      allEtudiants.forEach(e => {
        if (e._id) etuMap.set(String(e._id), e);
        if (e.etudiant_id) etuMap.set(String(e.etudiant_id), e);
        if (e.id) etuMap.set(String(e.id), e);
      });
      const map = {};
      notesExist.forEach(n => {
        const etuId = n.etudiant_id || (typeof n.etudiant === 'object' ? (n.etudiant._id || n.etudiant.etudiant_id) : (typeof n.etudiant === 'string' ? n.etudiant : ''));
        if (etuId) {
          map[String(etuId)] = { valeur: n.valeur !== undefined ? n.valeur : (n.note !== undefined ? n.note : n.controle_note), noteId: n._id, dirty: false, commentaire: n.commentaire || '' };
        }
      });
      setSaisieNotesMap(map);
      const matiere = matieres.find(m => memeId(m._id, controle.matiere_id) || memeId(m._id, saisieMatiere));
      const grpFiliere = saisieFiliere || controle.filiere_id || controle.filiere || matiere?.filiere_id || matiere?.filiere || '';
      const grpNiveau = saisieNiveau || controle.niveau_id || controle.niveau || matiere?.niveau_id || matiere?.niveau || '';
      const grpAnnee = saisieAnnee || controle.annee_universitaire_id || controle.annee_id || matiere?.annee_universitaire_id || matiere?.annee_id || '';
      let etus = [];
      // use populated from controle notes first (like Etudiants page)
      notesExist.forEach(n => {
        const etuObj = n.etudiant && typeof n.etudiant === 'object' ? n.etudiant : {};
        const etuId = n.etudiant_id || etuObj._id || etuObj.etudiant_id || etuObj.id || (typeof n.etudiant === 'string' ? n.etudiant : '');
        if (!etuId) return;
        if (etus.find(x => String(x._id) === String(etuId) || String(x.etudiant_id) === String(etuId))) return;
        const full = etuMap.get(String(etuId)) || allEtudiants.find(x => String(x._id) === String(etuId) || String(x.etudiant_id) === String(etuId) || String(x.etudiantId) === String(etuId)) || {};
        const nom = full.etudiant_nom || full.Nom || full.nom || full.nomFamille || full.nom_famille || full.lastName || full.lastname || full.name || etuObj.etudiant_nom || etuObj.Nom || etuObj.nom || etuObj.nomFamille || n.etudiant_nom || n.nom || n.etudiant?.Nom || '';
        const prenom = full.etudiant_prenom || full.Prénom || full.prenom || full.prenoms || full.firstName || full.firstname || full.etudiantPrenom || etuObj.etudiant_prenom || etuObj.Prénom || etuObj.prenom || n.etudiant_prenom || n.prenom || n.etudiant?.Prénom || '';
        const cne = full.etudiant_cne_masar || full.cne_masar || full.CNE || full.cne || full.massar || full.matricule || full.numeroEtudiant || full.numMatricule || full.codeApogee || full.code_apogee || full.code || etuObj.etudiant_cne_masar || etuObj.cne_masar || etuObj.CNE || etuObj.cne || etuObj.massar || etuObj.matricule || n.etudiant_cne_masar || n.cne_masar || n.cne || n.massar || n.matricule || n.CNE || '';
        etus.push({
          _id: etuId,
          etudiant_id: full.etudiant_id || etuObj.etudiant_id || etuId,
          etudiant_nom: nom,
          etudiant_prenom: prenom,
          etudiant_cne_masar: cne
        });
      });
      if (etus.length === 0) {
        inscriptions.forEach(ins => {
          const statut = ins.statut || ins.statutInscription || ins.etat || ins.statut_etudiant;
          const filiereId = ins.filiere?._id || ins.filiere_id;
          const niveauId = ins.niveau?._id || ins.niveau_id;
          const anneeId = ins.annee_universitaire_id || ins.annee_id || ins.anneeUniv || ins.annee;
          let ok = true;
          if (grpFiliere && filiereId && !memeId(filiereId, grpFiliere)) {
            const f1 = filieres.find(x => memeId(x._id, grpFiliere));
            if (!(f1 && ins.filiere_id && (memeId(ins.filiere_id, f1.filiere_id) || String(ins.filiere_id) === String(f1.filiere_id)))) ok = false;
          }
          if (ok && grpNiveau && niveauId && !memeId(niveauId, grpNiveau)) {
            const n1 = niveaux.find(x => memeId(x._id, grpNiveau));
            if (!(n1 && ins.niveau_id && (memeId(ins.niveau_id, n1.niveau_id) || String(ins.niveau_id) === String(n1.niveau_id)))) ok = false;
          }
          if (ok && grpAnnee && anneeId && !memeId(anneeId, grpAnnee)) ok = false;
          if (!ok && (grpFiliere || grpNiveau || grpAnnee)) return;
          const etu = ins.etudiant || ins.etudiantInfo || {};
          const etuId = etu._id || ins.etudiant_id || ins.etudiantId || ins.id_etudiant || ins.etudiant;
          if (!etuId) return;
          const full = etuMap.get(String(etuId)) || {};
          etus.push({
            _id: etuId,
            etudiant_id: full.etudiant_id || etu.etudiant_id || etuId,
            etudiant_nom: full.etudiant_nom || full.nom || full.nomFamille || etu.etudiant_nom || etu.nom || ins.etudiant_nom || ins.nom || '',
            etudiant_prenom: full.etudiant_prenom || full.prenom || etu.etudiant_prenom || etu.prenom || ins.etudiant_prenom || ins.prenom || '',
            etudiant_cne_masar: full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || etu.etudiant_cne_masar || etu.cne_masar || etu.cne || etu.massar || ins.etudiant_cne_masar || ins.cne_masar || ins.cne || ''
          });
        });
      }
      // merge students from existing notes if not present
      notesExist.forEach(n => {
        const etuObj = n.etudiant || n.etudiantInfo || {};
        const etuId = n.etudiant_id || etuObj._id || etuObj.etudiant_id || n.etudiantId;
        if (!etuId) return;
        if (etus.find(e => String(e._id) === String(etuId) || String(e.etudiant_id) === String(etuId))) return;
        const full = etuMap.get(String(etuId)) || {};
        etus.push({
          _id: etuId,
          etudiant_id: full.etudiant_id || etuObj.etudiant_id || etuId,
          etudiant_nom: full.etudiant_nom || full.nom || etuObj.etudiant_nom || etuObj.nom || n.etudiant_nom || n.nom || '',
          etudiant_prenom: full.etudiant_prenom || full.prenom || etuObj.etudiant_prenom || etuObj.prenom || n.etudiant_prenom || n.prenom || '',
          etudiant_cne_masar: full.etudiant_cne_masar || full.cne || full.cne_masar || full.massar || etuObj.etudiant_cne_masar || etuObj.cne || etuObj.massar || n.etudiant_cne_masar || n.cne_masar || n.cne || ''
        });
      });
      // if still empty, try broader
      if (etus.length === 0) {
        if (grpNiveau) {
          inscriptions.forEach(ins => {
            const niveauId = ins.niveau?._id || ins.niveau_id || ins.niveauId;
            const n1 = niveaux.find(x => memeId(x._id, grpNiveau) || memeId(x.niveau_id, grpNiveau));
            if (!(niveauId && (memeId(niveauId, grpNiveau) || (n1 && ins.niveau_id && (memeId(ins.niveau_id, n1.niveau_id) || String(ins.niveau_id) === String(n1.niveau_id)))))) return;
            const etu = ins.etudiant || ins.etudiantInfo || {};
            const etuId = etu._id || ins.etudiant_id || ins.etudiantId || ins.id_etudiant;
            if (!etuId) return;
            const full = etuMap.get(String(etuId)) || {};
            etus.push({
              _id: etuId,
              etudiant_id: full.etudiant_id || etu.etudiant_id || etuId,
              etudiant_nom: full.etudiant_nom || full.nom || full.nomFamille || etu.etudiant_nom || etu.nom || ins.etudiant_nom || ins.nom || '',
              etudiant_prenom: full.etudiant_prenom || full.prenom || etu.etudiant_prenom || etu.prenom || ins.etudiant_prenom || ins.prenom || '',
              etudiant_cne_masar: full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || etu.etudiant_cne_masar || etu.cne || ins.etudiant_cne_masar || ins.cne || ins.massar || ''
            });
          });
        }
        if (etus.length === 0) {
          inscriptions.forEach(ins => {
            const etu = ins.etudiant || ins.etudiantInfo || {};
            const etuId = etu._id || ins.etudiant_id || ins.etudiantId || ins.id_etudiant;
            if (!etuId) return;
            const full = etuMap.get(String(etuId)) || {};
            etus.push({
              _id: etuId,
              etudiant_id: full.etudiant_id || etu.etudiant_id || etuId,
              etudiant_nom: full.etudiant_nom || full.nom || full.nomFamille || etu.etudiant_nom || etu.nom || ins.etudiant_nom || ins.nom || '',
              etudiant_prenom: full.etudiant_prenom || full.prenom || etu.etudiant_prenom || etu.prenom || ins.etudiant_prenom || ins.prenom || '',
              etudiant_cne_masar: full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || etu.etudiant_cne_masar || etu.cne || ins.etudiant_cne_masar || ins.cne || ins.massar || ''
            });
          });
        }
      }
      // also try to fill from controle notes populated
      notesExist.forEach(n => {
        const etuObj = n.etudiant || (typeof n.etudiant === 'object' ? n.etudiant : {}) || {};
        const etuId = n.etudiant_id || etuObj._id || etuObj.etudiant_id || etuObj.id || (typeof n.etudiant === 'string' ? n.etudiant : '');
        if (!etuId) return;
        const key = String(etuId);
        const idx = etus.findIndex(e => String(e._id) === key || String(e.etudiant_id) === key);
        const full = etuMap.get(key) || etuMap.get(String(etuObj._id)) || etuMap.get(String(etuObj.etudiant_id)) || {};
        if (idx >= 0) {
          etus[idx] = {
            ...etus[idx],
            _id: etus[idx]._id || etuId,
            etudiant_id: etus[idx].etudiant_id || full.etudiant_id || etuObj.etudiant_id || etuId,
            etudiant_nom: etus[idx].etudiant_nom || full.etudiant_nom || full.nom || full.nomFamille || etuObj.etudiant_nom || etuObj.nom || etuObj.nomFamille || n.etudiant_nom || '',
            etudiant_prenom: etus[idx].etudiant_prenom || full.etudiant_prenom || full.prenom || etuObj.etudiant_prenom || etuObj.prenom || n.etudiant_prenom || '',
            etudiant_cne_masar: etus[idx].etudiant_cne_masar || full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || etuObj.etudiant_cne_masar || etuObj.cne || etuObj.cne_masar || etuObj.massar || n.etudiant_cne_masar || n.cne_masar || n.cne || ''
          };
        } else {
          etus.push({
            _id: etuId,
            etudiant_id: full.etudiant_id || etuObj.etudiant_id || etuId,
            etudiant_nom: full.etudiant_nom || full.nom || full.nomFamille || etuObj.etudiant_nom || etuObj.nom || etuObj.nomFamille || n.etudiant_nom || '',
            etudiant_prenom: full.etudiant_prenom || full.prenom || etuObj.etudiant_prenom || etuObj.prenom || n.etudiant_prenom || '',
            etudiant_cne_masar: full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || etuObj.etudiant_cne_masar || etuObj.cne || etuObj.cne_masar || etuObj.massar || n.etudiant_cne_masar || n.cne_masar || n.cne || ''
          });
        }
      });
      let uniq = Array.from(new Map(etus.map(e => [String(e._id || e.etudiant_id), e])).values());
      // final fill from all maps - same logic as Etudiants
      uniq = uniq.map(e => {
        const key = String(e._id || e.etudiant_id);
        let full = etuMap.get(key) || {};
        const anyE = allEtudiants.find(x => String(x._id) === key || String(x.etudiant_id) === key || String(x.etudiantId) === key) || {};
        if (Object.keys(anyE).length > 0) full = { ...full, ...anyE };
        const n2 = notesExist.find(n => {
          const nid = n.etudiant_id || (typeof n.etudiant === 'object' ? (n.etudiant._id || n.etudiant.etudiant_id) : (typeof n.etudiant === 'string' ? n.etudiant : ''));
          return String(nid) === key;
        }) || {};
        const etuObj2 = n2.etudiant && typeof n2.etudiant === 'object' ? n2.etudiant : {};
        if (Object.keys(etuObj2).length > 0) full = { ...full, ...etuObj2 };
        const ins2 = inscriptions.find(ins => {
          const ei = (ins.etudiant && (ins.etudiant._id || ins.etudiant.etudiant_id)) || ins.etudiant_id || ins.etudiantId;
          return String(ei) === key;
        }) || {};
        const ei2 = ins2.etudiant && typeof ins2.etudiant === 'object' ? ins2.etudiant : {};
        if (Object.keys(ei2).length > 0) full = { ...full, ...ei2 };
        const nom = e.etudiant_nom || full.etudiant_nom || full.Nom || full.nom || full.nomFamille || full.nom_famille || full.lastName || full.lastname || full.nom_f || full.nomFr || full.name || n2.etudiant_nom || n2.Nom || n2.nom || ins2.etudiant_nom || ins2.Nom || ins2.nom || etuObj2.etudiant_nom || etuObj2.Nom || etuObj2.nom || '';
        const prenom = e.etudiant_prenom || full.etudiant_prenom || full.Prénom || full.prenom || full.prenoms || full.firstName || full.firstname || full.etudiantPrenom || full.prenom1 || n2.etudiant_prenom || n2.Prénom || n2.prenom || ins2.etudiant_prenom || ins2.Prénom || ins2.prenom || etuObj2.etudiant_prenom || etuObj2.Prénom || etuObj2.prenom || '';
        const cne = e.etudiant_cne_masar || full.etudiant_cne_masar || full.cne_masar || full.CNE || full.cne || full.massar || full.matricule || full.numeroEtudiant || full.numMatricule || full.codeApogee || full.code_apogee || full.code || full.etudiant_code || n2.etudiant_cne_masar || n2.cne_masar || n2.CNE || n2.cne || n2.massar || n2.matricule || ins2.etudiant_cne_masar || ins2.cne_masar || ins2.CNE || ins2.cne || '';
        return {
          ...e,
          _id: e._id || e.etudiant_id || key,
          etudiant_id: e.etudiant_id || full.etudiant_id || key,
          etudiant_nom: nom,
          etudiant_prenom: prenom,
          etudiant_cne_masar: cne
        };
      });
      if (uniq.length === 0 && notesExist.length > 0) {
        notesExist.forEach(n => {
          const etuObj = n.etudiant || (typeof n.etudiant === 'object' ? n.etudiant : {}) || {};
          const etuId = n.etudiant_id || etuObj._id || etuObj.etudiant_id || (typeof n.etudiant === 'string' ? n.etudiant : '');
          if (!etuId) return;
          const full = etuMap.get(String(etuId)) || allEtudiants.find(x => String(x._id) === String(etuId) || String(x.etudiant_id) === String(etuId)) || {};
          uniq.push({
            _id: etuId,
            etudiant_id: full.etudiant_id || etuObj.etudiant_id || etuId,
            etudiant_nom: full.etudiant_nom || full.nom || full.nomFamille || etuObj.etudiant_nom || etuObj.nom || n.etudiant_nom || '',
            etudiant_prenom: full.etudiant_prenom || full.prenom || etuObj.etudiant_prenom || etuObj.prenom || n.etudiant_prenom || '',
            etudiant_cne_masar: full.etudiant_cne_masar || full.cne_masar || full.cne || full.massar || full.matricule || etuObj.etudiant_cne_masar || etuObj.cne_masar || etuObj.cne || etuObj.massar || etuObj.matricule || n.etudiant_cne_masar || ''
          });
        });
      }
      uniq.sort((a,b) => (a.etudiant_nom||'').localeCompare(b.etudiant_nom||'') || (a.etudiant_prenom||'').localeCompare(b.etudiant_prenom||''));
      setSaisieEtudiants(uniq);
    } catch (e) {
      console.error(e);
      try {
        const headers = getAuthHeaders();
        const [ctrlRes2] = await Promise.all([
          axios.get(`/api/notes/controle/${saisieControle}`, { headers })
        ]);
        const notesExist2 = ctrlRes2.data?.notes || [];
        const etus2 = [];
        notesExist2.forEach(n => {
          const etuObj = n.etudiant || (typeof n.etudiant === 'object' ? n.etudiant : {});
          const etuId = n.etudiant_id || etuObj._id || etuObj.etudiant_id || (typeof n.etudiant === 'string' ? n.etudiant : '');
          if (!etuId) return;
          etus2.push({
            _id: etuId,
            etudiant_id: etuObj.etudiant_id || etuId,
            etudiant_nom: etuObj.etudiant_nom || etuObj.nom || n.etudiant_nom || n.nom || '',
            etudiant_prenom: etuObj.etudiant_prenom || etuObj.prenom || n.etudiant_prenom || n.prenom || '',
            etudiant_cne_masar: etuObj.etudiant_cne_masar || etuObj.cne || etuObj.massar || n.etudiant_cne_masar || n.cne || ''
          });
        });
        setSaisieEtudiants(etus2);
      } catch (_) {
        setSaisieEtudiants([]);
      }
      setSaisieNotesMap({});
    } finally {
      setSaisieLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1 && peutSaisirNotes && saisieControle) {
      loadSaisieTable();
    } else if (tabValue === 1) {
      setSaisieEtudiants([]);
      setSaisieNotesMap({});
    }
  }, [tabValue, saisieControle, saisieFiliere, saisieNiveau, saisieAnnee]);

  const handleSaveSaisieLot = async () => {
    try {
      setSaisieSaving(true);
      const headers = getAuthHeaders();
      const controleSel = saisieControles.find(c => String(c._id) === String(saisieControle)) || {};
      const matiereSel = matieres.find(m => memeId(m._id, saisieMatiere) || memeId(m._id, controleSel.matiere_id));
      const notes = [];
      Object.keys(saisieNotesMap).forEach(etuId => {
        const entry = saisieNotesMap[etuId];
        if (entry && entry.dirty) {
          notes.push({
            etudiant_id: etuId,
            controle_id: saisieControle,
            matiere_id: saisieMatiere || controleSel.matiere_id || matiereSel?._id || '',
            valeur: entry.valeur === '' || entry.valeur === undefined || entry.valeur === null ? '' : Number(entry.valeur),
            commentaire: entry.commentaire || ''
          });
        }
      });
      if (notes.length === 0) {
        setSuccessMessage('Aucune modification à enregistrer');
        setTimeout(() => setSuccessMessage(''), 2000);
        setSaisieSaving(false);
        return;
      }
      await axios.post('/api/notes/saisie-lot', { notes }, { headers });
      setSuccessMessage('Notes enregistrées avec succès');
      setTimeout(() => setSuccessMessage(''), 2500);
      await loadSaisieTable();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaisieSaving(false);
    }
  };

  const loadControlesList = async () => {
  try {
    const headers = getAuthHeaders();
    const params = {};
    if (filterMatiere) params.matiere_id = filterMatiere;
    if (filterPeriode) params.periode_id = filterPeriode;
    const res = await axios.get('/api/notes/controles', { headers, params });
    let data = res.data || [];

    // Résoudre l'ObjectId du filtre vers le petit numéro attendu par les matières
    const filiereNum = (() => {
      if (!filterFiliere) return '';
      const f = filieres.find(x => memeId(x._id, filterFiliere) || memeId(x.filiere_id, filterFiliere));
      return f ? String(f.filiere_id || f._id) : String(filterFiliere);
    })();

    const niveauNum = (() => {
      if (!filterNiveau) return '';
      let n = niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
      if (!n) {
        for (const f of filieres) {
          if (Array.isArray(f.niveaux)) {
            const found = f.niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
            if (found) { n = found; break; }
          }
        }
      }
      return n ? String(n.niveau_id || n._id) : String(filterNiveau);
    })();

    data = data.filter((c) => {
      const matiere = matieres.find((m) => memeId(m._id, c.matiere_id));
      if (!matiere) return true;

      // Année : petit numéro des deux côtés
      if (filterAnnee) {
        const a = matiere.annee_universitaire_id || matiere.annee_id;
        if (a && !memeId(a, filterAnnee)) return false;
      }
      // Filière : comparer le petit numéro
      if (filiereNum) {
        const f = matiere.filiere_id || matiere.filiere;
        if (f && !memeId(f, filiereNum)) return false;
      }
      // Niveau : comparer le petit numéro
      if (niveauNum) {
        const n = matiere.niveau_id || matiere.niveau;
        if (n && !memeId(n, niveauNum)) return false;
      }
      // Module : ObjectId des deux côtés
      if (filterModule) {
        const mod = matiere.module_id || matiere.module;
        if (mod && !memeId(mod, filterModule)) return false;
      }
      return true;
    });

    setControlesList(data);
  } catch (err) {
    console.error('Erreur chargement contrôles:', err);
    setError('Impossible de charger la liste des contrôles');
  }
};
  const loadNiveauxByFiliere = async (filiereId) => {
    if (!filiereId) {
      setNiveauxFiltre([]);
      return;
    }
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/filieres/${filiereId}/niveaux`, { headers });
      setNiveauxFiltre(res.data?.data || res.data || []);
    } catch (err) {
      try {
        const filtered = niveaux.filter(n => n.filiere_id === filiereId || n.filiere === filiereId);
        setNiveauxFiltre(filtered);
      } catch (e) {
        setNiveauxFiltre([]);
      }
    }
  };

  const loadNiveauxByFiliereForm = async (filiereId) => {
    if (!filiereId) {
      setNiveauxForm([]);
      return;
    }
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/filieres/${filiereId}/niveaux`, { headers });
      setNiveauxForm(res.data?.data || res.data || []);
    } catch (err) {
      try {
        const filtered = niveaux.filter(n => n.filiere_id === filiereId || n.filiere === filiereId);
        setNiveauxForm(filtered);
      } catch (e) {
        setNiveauxForm([]);
      }
    }
  };

  const loadPeriodesByNiveau = async (niveauId) => {
    if (!niveauId) {
      setPeriodesFiltre([]);
      return;
    }
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/periodes/by-niveau/${niveauId}`, { headers });
      if (res.data && res.data.length > 0) {
        setPeriodesFiltre(res.data);
        return;
      }
    } catch (err) {
      // ignore
    }
    // fallback
    const filtered = periodes.filter(p => {
      if (p.niveau_id && String(p.niveau_id) === String(niveauId)) return true;
      if (p.niveau && String(p.niveau) === String(niveauId)) return true;
      if (Array.isArray(p.niveaux) && p.niveaux.some(n => String(n) === String(niveauId))) return true;
      if (Array.isArray(p.niveau_ids) && p.niveau_ids.some(n => String(n) === String(niveauId))) return true;
      if (typeof p.niveau === 'object' && (String(p.niveau._id) === String(niveauId) || String(p.niveau.niveau_id) === String(niveauId))) return true;
      // also try by filiere if matches
      const niv = niveaux.find(n => String(n._id) === String(niveauId));
      if (niv && p.filiere_id && String(p.filiere_id) === String(niv.filiere_id)) return true;
      if (niv && p.filiere && String(p.filiere) === String(niv.filiere_id)) return true;
      return false;
    });
    setPeriodesFiltre(filtered);
  };

  const loadPeriodesByNiveauForm = async (niveauId) => {
    if (!niveauId) {
      setPeriodesForm([]);
      return;
    }
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`/api/periodes/by-niveau/${niveauId}`, { headers });
      if (res.data && res.data.length > 0) {
        setPeriodesForm(res.data);
        return;
      }
    } catch (err) {
      // ignore
    }
    const filtered = periodes.filter(p => {
      if (p.niveau_id && String(p.niveau_id) === String(niveauId)) return true;
      if (p.niveau && String(p.niveau) === String(niveauId)) return true;
      if (Array.isArray(p.niveaux) && p.niveaux.some(n => String(n) === String(niveauId))) return true;
      if (Array.isArray(p.niveau_ids) && p.niveau_ids.some(n => String(n) === String(niveauId))) return true;
      if (typeof p.niveau === 'object' && (String(p.niveau._id) === String(niveauId) || String(p.niveau.niveau_id) === String(niveauId))) return true;
      const niv = niveaux.find(n => String(n._id) === String(niveauId));
      if (niv && p.filiere_id && String(p.filiere_id) === String(niv.filiere_id)) return true;
      if (niv && p.filiere && String(p.filiere) === String(niv.filiere_id)) return true;
      return false;
    });
    setPeriodesForm(filtered);
  };

  useEffect(() => {
    loadNiveauxByFiliere(filterFiliere);
  }, [filterFiliere]);

  useEffect(() => {
    if (!saisieFiliere) {
      setSaisieNiveaux([]);
      return;
    }
    const headers = getAuthHeaders();
    axios.get(`/api/filieres/${saisieFiliere}/niveaux`, { headers }).then(res => {
      setSaisieNiveaux(res.data?.data || res.data || []);
    }).catch(() => {
      try {
        const filtered = niveaux.filter(n => String(n.filiere_id) === String(saisieFiliere) || String(n.filiere) === String(saisieFiliere));
        setSaisieNiveaux(filtered);
      } catch (e) {
        setSaisieNiveaux([]);
      }
    });
  }, [saisieFiliere]);

  useEffect(() => {
    if (!saisieNiveau) {
      setSaisiePeriodes([]);
      return;
    }
    const headers = getAuthHeaders();
    const niv = niveaux.find(n => String(n._id) === String(saisieNiveau));
    const nivNum = niv?.niveau_id || niv?.niveauId || niv?.code || saisieNiveau;

    axios.get(`/api/periodes/by-niveau/${nivNum}`, { headers })
      .then(res => {
        let list = res.data || [];
        if (!list || list.length === 0) {
          list = periodes.filter(p => {
            const pNiv = p.niveau_id || p.niveau || p.niveauId;
            return String(pNiv) === String(nivNum);
          });
        }
        list = list.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
        setSaisiePeriodes(list);
      })
      .catch(() => {
        const list = periodes.filter(p => {
          const pNiv = p.niveau_id || p.niveau || p.niveauId;
          return String(pNiv) === String(nivNum);
        }).sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
        setSaisiePeriodes(list);
      });
  }, [saisieNiveau, periodes, niveaux]);

  useEffect(() => {
    loadNiveauxByFiliereForm(controleFormFiliere);
  }, [controleFormFiliere]);

  useEffect(() => {
  loadPeriodesByNiveau(filterNiveau);
}, [filterNiveau]);
  useEffect(() => {
  loadPeriodesByNiveauForm(controleFormNiveau);
}, [controleFormNiveau]);
  useEffect(() => {
    if (tabValue === 2 && peutSaisirNotes) {
      loadControlesList();
    }
  }, [tabValue, filterMatiere, filterPeriode, filterAnnee, filterFiliere, filterNiveau, filterModule]);

  useEffect(() => {
    const loadSaisieControlesList = async () => {
      if (!(tabValue === 1 && peutSaisirNotes)) {
        setSaisieControles([]);
        return;
      }
      try {
        const headers = getAuthHeaders();
        const res = await axios.get('/api/notes/controles', { headers });
        let data = res.data || [];

        const filiereNum = (() => {
          if (!saisieFiliere) return '';
          const f = filieres.find(x => memeId(x._id, saisieFiliere) || memeId(x.filiere_id, saisieFiliere));
          return f ? String(f.filiere_id || f._id) : String(saisieFiliere);
        })();

        const niveauNum = (() => {
          if (!saisieNiveau) return '';
          let n = niveaux.find(x => memeId(x._id, saisieNiveau) || memeId(x.niveau_id, saisieNiveau));
          if (!n) {
            for (const f of filieres) {
              if (Array.isArray(f.niveaux)) {
                const found = f.niveaux.find(x => memeId(x._id, saisieNiveau) || memeId(x.niveau_id, saisieNiveau));
                if (found) { n = found; break; }
              }
            }
          }
          return n ? String(n.niveau_id || n._id) : String(saisieNiveau);
        })();

        data = data.filter((c) => {
          if (saisieMatiere && !memeId(c.matiere_id, saisieMatiere)) return false;
          if (saisieModule && !memeId(c.module_id, saisieModule)) {
            const mat = matieres.find(m => memeId(m._id, c.matiere_id));
            const mod = mat ? (mat.module_id || mat.module) : null;
            if (!mod || !memeId(mod, saisieModule)) return false;
          }
          if (saisiePeriode && !memeId(c.periode_id, saisiePeriode)) return false;
          if (saisieAnnee) {
            const mat = matieres.find(m => memeId(m._id, c.matiere_id));
            const a = c.annee_universitaire_id || c.annee_id || mat?.annee_universitaire_id || mat?.annee_id;
            if (a && !memeId(a, saisieAnnee)) return false;
          }
          if (filiereNum) {
            const mat = matieres.find(m => memeId(m._id, c.matiere_id));
            const f = c.filiere_id || c.filiere || mat?.filiere_id || mat?.filiere;
            if (f && !memeId(f, filiereNum)) return false;
          }
          if (niveauNum) {
            const mat = matieres.find(m => memeId(m._id, c.matiere_id));
            const n = c.niveau_id || c.niveau || mat?.niveau_id || mat?.niveau;
            if (n && !memeId(n, niveauNum)) return false;
          }
          return true;
        });

        data.sort((a,b) => {
          const da = a.date || a.controle_date || 0;
          const db = b.date || b.controle_date || 0;
          if (da < db) return -1;
          if (da > db) return 1;
          return 0;
        });

        setSaisieControles(data);
      } catch (e) {
        console.error('Erreur chargement contrôles saisie:', e);
        setSaisieControles([]);
      }
    };
    loadSaisieControlesList();
  }, [tabValue, saisieMatiere, saisiePeriode, saisieModule, saisieNiveau, saisieFiliere, saisieAnnee, matieres, filieres, niveaux]);

  const openControleModal = (mode, controle = null) => {
    setControleMode(mode);
    if (mode === 'edit' && controle) {
      setSelectedControle(controle);
      const m = matieres.find(x => x._id === controle.matiere_id);
      setControleFormAnnee(m?.annee_universitaire_id || m?.annee_id || '');
      setControleFormFiliere(m?.filiere_id || '');
      setControleFormNiveau(m?.niveau_id || '');
      setControleFormModule(m?.module_id || '');
      setControleForm({
        intitule: controle.intitule || '',
        type: controle.type || 'CONTROLE_CONTINU',
        matiere_id: controle.matiere_id || '',
        periode_id: controle.periode_id || '',
        annee_universitaire_id: controle.annee_universitaire_id || controle.annee_id || m?.annee_universitaire_id || m?.annee_id || '',
        coefficient: controle.coefficient || controle.controle_coefficient || 1,
        session: controle.session || 'NORMALE',
        controle_type: controle.controle_type || 0,
        date: controle.date ? controle.date.substring(0, 10) : (controle.controle_date ? controle.controle_date.substring(0, 10) : ''),
        controle_h_debut: controle.controle_h_debut || controle.heureDebut || '',
        controle_h_fin: controle.controle_h_fin || controle.heureFin || '',
        salle_id: controle.salle_id || '',
        estActif: controle.estActif !== undefined ? controle.estActif : true
      });
    } else {
      setSelectedControle(null);
      const mInit = matieres.find(x => x._id === filterMatiere);
      setControleFormAnnee(filterAnnee || mInit?.annee_universitaire_id || mInit?.annee_id || '');
      setControleFormFiliere(filterFiliere || mInit?.filiere_id || '');
      setControleFormNiveau(filterNiveau || mInit?.niveau_id || '');
      setControleFormModule(filterModule || mInit?.module_id || '');
      setControleForm({
        intitule: '',
        type: 'CONTROLE_CONTINU',
        matiere_id: filterMatiere || '',
        periode_id: filterPeriode || '',
        annee_universitaire_id: filterAnnee || mInit?.annee_universitaire_id || mInit?.annee_id || '1',
        coefficient: 1,
        session: 'NORMALE',
        controle_type: 0,
        date: '',
        controle_h_debut: '',
        controle_h_fin: '',
        salle_id: '',
        estActif: true
      });
    }
    setModalControleOpen(true);
  };

  const closeControleModal = () => {
    setModalControleOpen(false);
    setSelectedControle(null);
    setControleFormAnnee('');
    setControleFormFiliere('');
    setControleFormNiveau('');
    setControleFormModule('');
  };

  const saveControle = async () => {
    try {
      const headers = getAuthHeaders();
      const memeId = (a, b) =>
        a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
      if (controleMode === 'add') {
        await axios.post('/api/notes/controles', controleForm, { headers });
        setSuccessMessage('Contrôle créé avec succès');
      } else {
        await axios.put(`/api/notes/controles/${selectedControle._id}`, controleForm, { headers });
        setSuccessMessage('Contrôle modifié avec succès');
      }
      closeControleModal();
      loadControlesList();
      if (selectedMatiere) {
        loadControlesByMatiere(selectedMatiere);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'enregistrement du contrôle");
    }
  };

  const deleteControle = async (id) => {
    if (!window.confirm('Supprimer ce contrôle ? Cette action est irréversible.')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/notes/controles/${id}`, { headers });
      setSuccessMessage('Contrôle supprimé avec succès');
      loadControlesList();
      if (selectedMatiere) {
        loadControlesByMatiere(selectedMatiere);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };
 console.log('[render] modules state:', modules.length, 'matieres state:', matieres.length, 'anneesUniv:', anneesUniv.length);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📊 Gestion des Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEtudiant ? 'Mes notes et mon bulletin' : isEnseignant ? 'Saisie et gestion des notes' : 'Administration des notes'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          {peutSaisirNotes && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal('add')}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Nouvelle Note
            </Button>
          )}
        </Stack>
      </Box>

      {/* Onglets */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="📋 Bulletin" />
          {peutSaisirNotes && <Tab label="📝 Saisie des notes" />}
          {peutSaisirNotes && <Tab label="🗂 Gestion des contrôles" />}
          {peutSaisirNotes && <Tab label="📊 Statistiques" />}
        </Tabs>
      </Paper>

      {/* ========== TAB 1 : BULLETIN ========== */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            👨‍🎓 Bulletin de notes
          </Typography>

          {/* Sélection de l'étudiant (navigation : aussi pour la direction générale et le coordinateur pédagogique) */}
          {(peutSaisirNotes || isDirectionGenerale || isCoordinateurPedagogique || isResponsableScolarite) && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Étudiant</InputLabel>
              <Select
                value={selectedEtudiant}
                onChange={(e) => loadBulletin(e.target.value)}
                label="Étudiant"
              >
                <MenuItem value="">Sélectionner un étudiant</MenuItem>
                {etudiants.map((e) => (
                  <MenuItem key={e.etudiant_id} value={e.etudiant_id}>
                    {e.etudiant_nom} {e.etudiant_prenom} ({e.etudiant_cne_masar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Affichage du bulletin */}
          {bulletin ? (
            <Box>
              {/* En-tête du bulletin */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Étudiant :</strong> {bulletin.etudiant?.etudiant_nom} {bulletin.etudiant?.etudiant_prenom}</Typography>
                    <Typography variant="body2"><strong>CNE :</strong> {bulletin.etudiant?.etudiant_cne_masar}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Moyenne Générale :</strong> <strong style={{ color: '#667eea', fontSize: '1.2rem' }}>{bulletin.moyenneGenerale}/20</strong></Typography>
                    <Chip
                      label={getMention(parseFloat(bulletin.moyenneGenerale)).label}
                      sx={{ bgcolor: getMention(parseFloat(bulletin.moyenneGenerale)).color, color: 'white', mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Tableau des résultats */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Matière</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Crédits</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Moyenne</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Mention</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulletin.resultats?.map((r, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{r.matiere}</TableCell>
                        <TableCell>
                          <Chip label={r.code} size="small" sx={{ fontFamily: 'monospace' }} />
                        </TableCell>
                        <TableCell>{r.credits || '-'}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color={r.estValide ? '#10b981' : '#ef4444'}>
                            {r.moyenne}/20
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {r.estValide && <Chip label={r.mention} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.estValide ? '✅ Validé' : '❌ Non validé'}
                            size="small"
                            sx={{ bgcolor: r.estValide ? '#dcfce7' : '#fee2e2', color: r.estValide ? '#166534' : '#991b1b' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Résumé */}
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Total crédits :</strong> {bulletin.totalCredits || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Crédits validés :</strong> {bulletin.creditsValides || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2"><strong>Mention :</strong> <Chip label={bulletin.mention} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} /></Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">
                {isEtudiant ? 'Chargement de vos notes...' : 'Sélectionnez un étudiant pour voir son bulletin'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ========== TAB 2 : SAISIE DES NOTES ========== */}
      {tabValue === 1 && peutSaisirNotes && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              📝 Saisie des notes
            </Typography>
            <Button
              variant="contained"
              disabled={saisieSaving || Object.keys(saisieNotesMap).filter(k => saisieNotesMap[k]?.dirty).length === 0}
              onClick={handleSaveSaisieLot}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {saisieSaving ? 'Enregistrement...' : 'Enregistrer tout'}
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-annee-label">Année</InputLabel>
                <Select labelId="saisie-annee-label" value={saisieAnnee} onChange={(e) => { setSaisieAnnee(e.target.value); setSaisieFiliere(''); setSaisieNiveau(''); setSaisiePeriode(''); setSaisieModule(''); setSaisieMatiere(''); setSaisieControle(''); setSaisieNiveaux([]); setSaisiePeriodes([]); setSaisieControles([]); }} label="Année">
                  <MenuItem value="">Toutes</MenuItem>
                  {anneesUniv.map((a) => (
                    <MenuItem key={a._id} value={a.annee_id || a._id}>{a.libelle || a.intitule || a.code || a.annee_id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-filiere-label">Filière</InputLabel>
                <Select labelId="saisie-filiere-label" value={saisieFiliere} onChange={(e) => { setSaisieFiliere(e.target.value); setSaisieNiveau(''); setSaisiePeriode(''); setSaisieModule(''); setSaisieMatiere(''); setSaisieControle(''); setSaisiePeriodes([]); setSaisieControles([]); }} label="Filière">
                  <MenuItem value="">Toutes</MenuItem>
                  {filieres.filter(f => !saisieAnnee || String(f.annee_id) === String(saisieAnnee) || String(f.annee_universitaire_id) === String(saisieAnnee)).map((f) => (
                    <MenuItem key={f._id} value={f._id}>{f.intitule || f.libelle || f.code_filiere || f.code || f.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-niveau-label">Niveau</InputLabel>
                <Select labelId="saisie-niveau-label" value={saisieNiveau} onChange={(e) => { setSaisieNiveau(e.target.value); setSaisiePeriode(''); setSaisieModule(''); setSaisieMatiere(''); setSaisieControle(''); setSaisieControles([]); }} label="Niveau">
                  <MenuItem value="">Tous</MenuItem>
                  {(saisieNiveaux.length > 0 ? saisieNiveaux : niveaux.filter(n => !saisieFiliere || String(n.filiere_id) === String(saisieFiliere) || String(n.filiere) === String(saisieFiliere))).map((n) => (
                    <MenuItem key={n._id} value={n._id}>{n.intitule || n.libelle || n.code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-periode-label">Période</InputLabel>
                <Select labelId="saisie-periode-label" value={saisiePeriode} onChange={(e) => { setSaisiePeriode(e.target.value); setSaisieModule(''); setSaisieMatiere(''); setSaisieControle(''); }} label="Période">
                  <MenuItem value="">Toutes</MenuItem>
                  {(saisiePeriodes.length > 0 ? saisiePeriodes : periodes.filter(p => {
                    if (!saisieNiveau) return false;
                    if (p.niveau_id && String(p.niveau_id) === String(saisieNiveau)) return true;
                    if (p.niveau && String(p.niveau) === String(saisieNiveau)) return true;
                    if (Array.isArray(p.niveaux) && p.niveaux.some(n => String(n) === String(saisieNiveau))) return true;
                    if (Array.isArray(p.niveau_ids) && p.niveau_ids.some(n => String(n) === String(saisieNiveau))) return true;
                    if (typeof p.niveau === 'object' && (String(p.niveau._id) === String(saisieNiveau) || String(p.niveau.niveau_id) === String(saisieNiveau))) return true;
                    return false;
                  })).map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.nom || p.intitule || p.libelle}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-module-label">Module</InputLabel>
                <Select labelId="saisie-module-label" value={saisieModule} onChange={(e) => { setSaisieModule(e.target.value); setSaisieMatiere(''); setSaisieControle(''); }} label="Module">
                  <MenuItem value="">Tous</MenuItem>
                  {(() => {
                    const filiereNum = (() => {
                      if (!saisieFiliere) return '';
                      const f = filieres.find(x => memeId(x._id, saisieFiliere) || memeId(x.filiere_id, saisieFiliere));
                      return f ? String(f.filiere_id || f._id) : String(saisieFiliere);
                    })();
                    const niveauNum = (() => {
                      if (!saisieNiveau) return '';
                      let n = niveaux.find(x => memeId(x._id, saisieNiveau) || memeId(x.niveau_id, saisieNiveau));
                      if (!n) {
                        for (const f of filieres) {
                          if (Array.isArray(f.niveaux)) {
                            const found = f.niveaux.find(x => memeId(x._id, saisieNiveau) || memeId(x.niveau_id, saisieNiveau));
                            if (found) { n = found; break; }
                          }
                        }
                      }
                      return n ? String(n.niveau_id || n._id) : String(saisieNiveau);
                    })();
                    let list = (modules || []).slice();
                    (matieres || []).forEach(mt => {
                      const mid = mt.module_id || mt.module;
                      if (!mid) return;
                      if (!list.find(x => String(x._id) === String(mid))) {
                        list.push({ _id: mid, intitule: mt.module_nom || mt.module_libelle || mt.module || mid, code: mt.module_code || '', niveau_id: mt.niveau_id, filiere_id: mt.filiere_id, annee_universitaire_id: mt.annee_universitaire_id, periode_id: mt.periode_id, module_id: mid, module: mid });
                      }
                    });
                    return list.filter(m => {
                      if (saisiePeriode) {
                        let okPer = String(m.periode_id) === String(saisiePeriode) || String(m.periode) === String(saisiePeriode);
                        if (!okPer) {
                          okPer = (matieres || []).some(mt => {
                            const sameMod = String(mt.module_id) === String(m._id) || String(mt.module) === String(m._id);
                            const samePer = String(mt.periode_id) === String(saisiePeriode) || String(mt.periode) === String(saisiePeriode);
                            return sameMod && samePer;
                          });
                        }
                        if (!okPer) return false;
                      }
                      if (saisieNiveau) {
                        let okN = String(m.niveau_id) === String(saisieNiveau) || String(m.niveau_id) === String(niveauNum) || String(m.niveau) === String(saisieNiveau) || String(m.niveau) === String(niveauNum);
                        if (!okN) {
                          okN = (matieres || []).some(mt => {
                            const sameMod = String(mt.module_id) === String(m._id) || String(mt.module) === String(m._id);
                            const sameN = String(mt.niveau_id) === String(saisieNiveau) || String(mt.niveau_id) === String(niveauNum) || String(mt.niveau) === String(saisieNiveau) || String(mt.niveau) === String(niveauNum);
                            return sameMod && sameN;
                          });
                        }
                        if (!okN) return false;
                      }
                      if (saisieFiliere) {
                        let okF = String(m.filiere_id) === String(saisieFiliere) || String(m.filiere_id) === String(filiereNum) || String(m.filiere) === String(saisieFiliere) || String(m.filiere) === String(filiereNum);
                        if (!okF) {
                          okF = (matieres || []).some(mt => {
                            const sameMod = String(mt.module_id) === String(m._id) || String(mt.module) === String(m._id);
                            const sameF = String(mt.filiere_id) === String(saisieFiliere) || String(mt.filiere_id) === String(filiereNum) || String(mt.filiere) === String(saisieFiliere) || String(mt.filiere) === String(filiereNum);
                            return sameMod && sameF;
                          });
                        }
                        if (!okF) return false;
                      }
                      if (saisieAnnee) {
                        let okA = String(m.annee_universitaire_id) === String(saisieAnnee) || String(m.annee_id) === String(saisieAnnee);
                        if (!okA) {
                          okA = (matieres || []).some(mt => {
                            const sameMod = String(mt.module_id) === String(m._id) || String(mt.module) === String(m._id);
                            const sameA = String(mt.annee_universitaire_id) === String(saisieAnnee) || String(mt.annee_id) === String(saisieAnnee);
                            return sameMod && sameA;
                          });
                        }
                        if (!okA) return false;
                      }
                      return true;
                    }).map((m) => (
                      <MenuItem key={m._id} value={m._id}>{m.intitule || m.nom || m.libelle || m.code || m._id}</MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="saisie-matiere-label">Matière</InputLabel>
                <Select labelId="saisie-matiere-label" value={saisieMatiere} onChange={(e) => { setSaisieMatiere(e.target.value); setSaisieControle(''); }} label="Matière">
                  <MenuItem value="">Toutes</MenuItem>
                  {(matieres || []).filter(m => {
                    if (saisieModule && !(String(m.module_id) === String(saisieModule) || String(m.module) === String(saisieModule))) return false;
                    if (saisieNiveau) {
                      let okN = String(m.niveau_id) === String(saisieNiveau);
                      if (!okN) {
                        let n = niveaux.find(x => memeId(x._id, saisieNiveau));
                        if (n) okN = String(m.niveau_id) === String(n.niveau_id);
                      }
                      if (!okN) return false;
                    }
                    if (saisieFiliere) {
                      let okF = String(m.filiere_id) === String(saisieFiliere);
                      if (!okF) {
                        const f = filieres.find(x => memeId(x._id, saisieFiliere));
                        if (f) okF = String(m.filiere_id) === String(f.filiere_id);
                      }
                      if (!okF) return false;
                    }
                    if (saisieAnnee && !(String(m.annee_universitaire_id) === String(saisieAnnee) || String(m.annee_id) === String(saisieAnnee))) return false;
                    if (saisiePeriode && !(String(m.periode_id) === String(saisiePeriode) || String(m.periode) === String(saisiePeriode))) return false;
                    return true;
                  }).map((m) => (
                    <MenuItem key={m._id} value={m._id}>{m.nom || m.libelle || m.intitule || m.code || m._id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <FormControl fullWidth>
                <InputLabel id="saisie-controle-label">Contrôle</InputLabel>
                <Select labelId="saisie-controle-label" value={saisieControle} onChange={(e) => setSaisieControle(e.target.value)} label="Contrôle">
                  <MenuItem value="">Sélectionner un contrôle</MenuItem>
                  {saisieControles.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.intitule || c.nom || c.libelle || c.type || c._id} {c.date ? `- ${new Date(c.date).toLocaleDateString()}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {saisieControle && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Étudiants : {saisieEtudiants.length} | Modifiés : {Object.keys(saisieNotesMap).filter(k => saisieNotesMap[k]?.dirty).length}
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Matricule/CNE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Nom & Prénom</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Note /20</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Commentaire</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {saisieLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {saisieEtudiants.map((etu) => {
                          const key = String(etu._id || etu.etudiant_id);
                          const entry = saisieNotesMap[key] || {};
                          const val = entry.valeur !== undefined && entry.valeur !== null ? entry.valeur : '';
                          const num = val === '' ? NaN : Number(val);
                          const valide = !isNaN(num) && num >= 0 && num <= 20;
                          const isValidNote = valide && num >= 10;
                          return (
                            <TableRow key={key} hover>
                              <TableCell>{etu.etudiant_cne_masar || etu.cne_masar || etu.cne || etu.massar || etu.matricule || etu.numMatricule || etu.numeroEtudiant || etu.codeApogee || etu.code || '-'}</TableCell>
                              <TableCell>{(etu.etudiant_nom || etu.Nom || etu.nom || etu.nomFamille || etu.nom_famille || etu.lastName || etu.lastname || etu.name || '')} {(etu.etudiant_prenom || etu.Prénom || etu.prenom || etu.prenoms || etu.firstName || etu.firstname || '')}</TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ min: 0, max: 20, step: 0.25 }}
                                  value={val}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSaisieNotesMap(prev => ({
                                      ...prev,
                                      [key]: { ...(prev[key] || {}), valeur: v, dirty: true }
                                    }));
                                  }}
                                  error={val !== '' && (!valide)}
                                  helperText={val !== '' && !valide ? '0-20' : ''}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={entry.commentaire || ''}
                                  onChange={(e) => {
                                    setSaisieNotesMap(prev => ({
                                      ...prev,
                                      [key]: { ...(prev[key] || {}), commentaire: e.target.value, dirty: true }
                                    }));
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {val === '' ? <Chip label="Non saisi" size="small" /> : isValidNote ? <Chip label="✅ Validé" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534' }} /> : <Chip label="❌ Non validé" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {saisieEtudiants.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                              <Typography color="text.secondary">Aucun étudiant trouvé pour ce groupe</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* ========== TAB 2 : GESTION DES CONTRÔLES ========== */}
      {tabValue === 2 && peutSaisirNotes && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              🗂 Gestion des contrôles
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openControleModal('add')}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Nouveau contrôle
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="annee-label">Année</InputLabel>
                <Select labelId="annee-label" value={filterAnnee} onChange={(e) => { setFilterAnnee(e.target.value); setFilterFiliere(''); setFilterNiveau(''); setFilterPeriode(''); setFilterModule(''); setFilterMatiere(''); setNiveauxFiltre([]); }} label="Année">
                  <MenuItem value="">Toutes</MenuItem>
                  {anneesUniv.map((a) => (
                    <MenuItem key={a._id} value={a.annee_id || a._id}>{a.libelle || a.intitule || a.code || a.annee_id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="filiere-label">Filière</InputLabel>
                <Select labelId="filiere-label" value={filterFiliere} onChange={(e) => { setFilterFiliere(e.target.value); setFilterNiveau(''); setFilterPeriode(''); setFilterModule(''); setFilterMatiere(''); loadNiveauxByFiliere(e.target.value); }} label="Filière">
                  <MenuItem value="">Toutes</MenuItem>
                  {filieres.filter(f => !filterAnnee || String(f.annee_id) === String(filterAnnee) || String(f.annee_universitaire_id) === String(filterAnnee)).map((f) => (
                    <MenuItem key={f._id} value={f._id}>{f.intitule || f.libelle || f.code_filiere || f.code || f.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="niveau-label">Niveau</InputLabel>
                <Select labelId="niveau-label" value={filterNiveau} onChange={(e) => { const v=e.target.value; setFilterNiveau(v); setFilterPeriode(''); setFilterModule(''); setFilterMatiere(''); loadPeriodesByNiveau(v); }} label="Niveau">
                  <MenuItem value="">Tous</MenuItem>
                  {(niveauxFiltre.length > 0 ? niveauxFiltre : niveaux.filter(n => !filterFiliere || String(n.filiere_id) === String(filterFiliere) || String(n.filiere) === String(filterFiliere))).map((n) => (
                    <MenuItem key={n._id} value={n._id}>{n.intitule || n.libelle || n.code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <FormControl fullWidth>
                <InputLabel id="periode-label">Période</InputLabel>
                <Select labelId="periode-label" value={filterPeriode} onChange={(e) => { const v=e.target.value; setFilterPeriode(v); setFilterModule(''); setFilterMatiere(''); if (v) { const headers=getAuthHeaders(); axios.get('/api/modules/periode/'+v,{headers}).then(r=>{if(r.data?.length)setModules(prev=>{const map=new Map(prev.map(x=>[x._id,x])); r.data.forEach(x=>map.set(x._id,x)); return Array.from(map.values());});}).catch(()=>{}); axios.get('/api/matieres/by-periode/'+v,{headers}).then(r=>{if(r.data?.length)setMatieres(prev=>{const map=new Map(prev.map(x=>[x._id,x])); r.data.forEach(x=>map.set(x._id,x)); return Array.from(map.values());});}).catch(()=>{}); } }} label="Période">
                  <MenuItem value="">Toutes</MenuItem>
                  {(periodesFiltre.length > 0 ? periodesFiltre : periodes.filter(p => {
                    if (!filterNiveau) return false;
                    if (p.niveau_id && String(p.niveau_id) === String(filterNiveau)) return true;
                    if (p.niveau && String(p.niveau) === String(filterNiveau)) return true;
                    if (Array.isArray(p.niveaux) && p.niveaux.some(n => String(n) === String(filterNiveau))) return true;
                    if (Array.isArray(p.niveau_ids) && p.niveau_ids.some(n => String(n) === String(filterNiveau))) return true;
                    if (typeof p.niveau === 'object' && (String(p.niveau._id) === String(filterNiveau) || String(p.niveau.niveau_id) === String(filterNiveau))) return true;
                    return false;
                  })).map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.nom || p.intitule || p.libelle}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
  <FormControl fullWidth>
    <InputLabel id="module-label">Module</InputLabel>
    <Select labelId="module-label" value={filterModule} onChange={(e) => { setFilterModule(e.target.value); setFilterMatiere(''); }} label="Module">
      <MenuItem value="">Tous</MenuItem>
            {(() => {
        const filiereNum = (() => {
          if (!filterFiliere) return '';
          const f = filieres.find(x => memeId(x._id, filterFiliere) || memeId(x.filiere_id, filterFiliere));
          return f ? String(f.filiere_id || f._id) : String(filterFiliere);
        })();
        const niveauNum = (() => {
          if (!filterNiveau) return '';
          let n = niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
          if (!n) {
            for (const f of filieres) {
              if (Array.isArray(f.niveaux)) {
                const found = f.niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
                if (found) { n = found; break; }
              }
            }
          }
          return n ? String(n.niveau_id || n._id) : String(filterNiveau);
        })();

        return modules
          .filter(m => {
            if (filterPeriode && m.periode_id && !memeId(m.periode_id, filterPeriode)) return false;
            if (niveauNum && m.niveau_id && !memeId(m.niveau_id, niveauNum)) return false;
            if (filiereNum && m.filiere_id && !memeId(m.filiere_id, filiereNum)) return false;
            if (filterAnnee && m.annee_universitaire_id && !memeId(m.annee_universitaire_id, filterAnnee)) return false;
            return true;
          })
          .map(m => (
            <MenuItem key={m._id} value={m._id}>{m.nom || m.intitule || m.code || m.libelle}</MenuItem>
          ));
      })()}
    </Select>
  </FormControl>
</Grid>
            <Grid item xs={12} md={4} lg={2}>
  <FormControl fullWidth>
    <InputLabel id="matiere-label">Matière</InputLabel>
    <Select labelId="matiere-label" value={filterMatiere} onChange={(e) => setFilterMatiere(e.target.value)} label="Matière">
      <MenuItem value="">Toutes</MenuItem>
            {(() => {
        const filiereNum = (() => {
          if (!filterFiliere) return '';
          const f = filieres.find(x => memeId(x._id, filterFiliere) || memeId(x.filiere_id, filterFiliere));
          return f ? String(f.filiere_id || f._id) : String(filterFiliere);
        })();
        const niveauNum = (() => {
          if (!filterNiveau) return '';
          let n = niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
          if (!n) {
            for (const f of filieres) {
              if (Array.isArray(f.niveaux)) {
                const found = f.niveaux.find(x => memeId(x._id, filterNiveau) || memeId(x.niveau_id, filterNiveau));
                if (found) { n = found; break; }
              }
            }
          }
          return n ? String(n.niveau_id || n._id) : String(filterNiveau);
        })();

        return matieres
          .filter(m =>
            (!filterModule || !m.module_id || memeId(m.module_id, filterModule) || memeId(m.module, filterModule)) &&
            (!niveauNum || !m.niveau_id || memeId(m.niveau_id, niveauNum)) &&
            (!filiereNum || !m.filiere_id || memeId(m.filiere_id, filiereNum)) &&
            (!filterAnnee || !m.annee_universitaire_id || memeId(m.annee_universitaire_id, filterAnnee) || memeId(m.annee_id, filterAnnee)) &&
            (!filterPeriode || !m.periode_id || memeId(m.periode_id, filterPeriode) || memeId(m.periode, filterPeriode))
          )
          .map(m => (
            <MenuItem key={m._id} value={m._id}>{m.nom || m.code} {m.code ? `(${m.code})` : ''}</MenuItem>
          ));
      })()}
    </Select>
  </FormControl>
</Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Intitulé</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Matière</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Période</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Session</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Coefficient</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Horaire</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {controlesList.map((c) => {
                  const matiere = matieres.find(m => m._id === c.matiere_id);
                  const periode = periodes.find(p => p._id === c.periode_id);
                  return (
                    <TableRow key={c._id} hover>
                      <TableCell>{c.intitule}</TableCell>
                      <TableCell>{matiere ? `${matiere.code} - ${matiere.nom}` : '-'}</TableCell>
                      <TableCell>{periode ? (periode.nom || periode.intitule || periode.libelle) : '-'}</TableCell>
                      <TableCell><Chip label={c.type} size="small" sx={{ bgcolor: getTypeColor(c.type), color: 'white' }} /></TableCell>
                      <TableCell><Chip label={c.session || 'NORMALE'} size="small" /></TableCell>
                      <TableCell>{c.coefficient || c.controle_coefficient || 1}</TableCell>
                      <TableCell>{c.date ? new Date(c.date).toLocaleDateString() : (c.controle_date ? new Date(c.controle_date).toLocaleDateString() : '-')}</TableCell>
                      <TableCell>{(c.controle_h_debut || c.heureDebut) ? `${c.controle_h_debut || c.heureDebut}${(c.controle_h_fin || c.heureFin) ? ' - ' + (c.controle_h_fin || c.heureFin) : ''}` : '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Modifier"><IconButton size="small" color="warning" onClick={() => openControleModal('edit', c)}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Supprimer"><IconButton size="small" color="error" onClick={() => deleteControle(c._id)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {controlesList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">Aucun contrôle trouvé. Créez-en un avec le bouton "Nouveau contrôle".</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ========== TAB 3 : STATISTIQUES ========== */}
      {tabValue === 3 && peutSaisirNotes && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
            📊 Statistiques des notes
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total des notes</Typography>
                  <Typography variant="h3" fontWeight="bold">0</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Taux de réussite</Typography>
                  <Typography variant="h3" fontWeight="bold">0%</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Moyenne générale</Typography>
                  <Typography variant="h3" fontWeight="bold">0/20</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center', py: 5, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography color="text.secondary">
              Sélectionnez une matière dans l'onglet "Saisie des notes" pour voir les statistiques
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ========== MODAL GESTION CONTRÔLE ========== */}
      <Dialog open={modalControleOpen} onClose={closeControleModal} maxWidth="md" fullWidth>
        <DialogTitle>{controleMode === 'add' ? '➕ Créer un contrôle' : '✏️ Modifier le contrôle'}</DialogTitle>
        <DialogContent>
         
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Intitulé *" value={controleForm.intitule} onChange={(e) => setControleForm({ ...controleForm, intitule: e.target.value })} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type *</InputLabel>
                <Select value={controleForm.type} label="Type *" onChange={(e) => setControleForm({ ...controleForm, type: e.target.value })}>
                  <MenuItem value="CONTROLE_CONTINU">Contrôle Continu</MenuItem>
                  <MenuItem value="EXAMEN_INTERMEDIAIRE">Examen Intermédiaire</MenuItem>
                  <MenuItem value="EXAMEN_FINAL">Examen Final</MenuItem>
                  <MenuItem value="TP">TP</MenuItem>
                  <MenuItem value="TD">TD</MenuItem>
                  <MenuItem value="PROJET">Projet</MenuItem>
                  <MenuItem value="RATTRAPAGE">Rattrapage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="annee-label-form">Année</InputLabel>
                <Select labelId="annee-label-form" value={controleFormAnnee} label="Année" onChange={(e) => { setControleFormAnnee(e.target.value); setControleFormFiliere(''); setControleFormNiveau(''); setControleFormModule(''); setNiveauxForm([]); setControleForm({ ...controleForm, matiere_id: '', periode_id: '', annee_universitaire_id: e.target.value }); }}>
                  <MenuItem value="">Sélectionner</MenuItem>
                  {anneesUniv.map((a) => (<MenuItem key={a._id} value={a.annee_id || a._id}>{a.libelle || a.intitule || a.code || a.annee_id}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="filiere-label-form">Filière</InputLabel>
                <Select labelId="filiere-label-form" value={controleFormFiliere} label="Filière" onChange={(e) => { setControleFormFiliere(e.target.value); setControleFormNiveau(''); setControleFormModule(''); setControleForm({ ...controleForm, matiere_id: '' }); loadNiveauxByFiliereForm(e.target.value); }}>
                  <MenuItem value="">Sélectionner</MenuItem>
                  {filieres.filter(f => !controleFormAnnee || String(f.annee_id) === String(controleFormAnnee) || String(f.annee_universitaire_id) === String(controleFormAnnee)).map((f) => (<MenuItem key={f._id} value={f._id}>{f.intitule || f.libelle || f.code_filiere || f.code || f.nom}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="niveau-label-form">Niveau</InputLabel>
                <Select labelId="niveau-label-form" value={controleFormNiveau} label="Niveau" onChange={(e) => { const v=e.target.value; setControleFormNiveau(v); setControleFormModule(''); setControleForm({ ...controleForm, matiere_id: '', periode_id: '' }); loadPeriodesByNiveauForm(v); }}>
                  <MenuItem value="">Sélectionner</MenuItem>
                  {(niveauxForm.length > 0 ? niveauxForm : niveaux.filter(n => !controleFormFiliere || String(n.filiere_id) === String(controleFormFiliere) || String(n.filiere) === String(controleFormFiliere))).map((n) => (<MenuItem key={n._id} value={n._id}>{n.intitule || n.libelle || n.code}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
  <FormControl fullWidth>
    <InputLabel id="periode-label-form">Période *</InputLabel>
    <Select
      labelId="periode-label-form"
      value={controleForm.periode_id || ''}
      label="Période *"
      onChange={(e) => {
        const v = e.target.value;
        // ⚠️ UN SEUL setState : on met à jour période + reset module/matière en même temps
        setControleForm((prev) => ({
          ...prev,
          periode_id: v,
          matiere_id: ''
        }));
        setControleFormModule('');
      }}
    >
      <MenuItem value="">Sélectionner</MenuItem>
      {(periodesForm.length > 0
        ? periodesForm
        : periodes.filter((p) => {
            if (!controleFormNiveau) return false;
            if (p.niveau_id && String(p.niveau_id) === String(controleFormNiveau)) return true;
            if (p.niveau && String(p.niveau) === String(controleFormNiveau)) return true;
            if (Array.isArray(p.niveaux) && p.niveaux.some((n) => String(n) === String(controleFormNiveau))) return true;
            if (Array.isArray(p.niveau_ids) && p.niveau_ids.some((n) => String(n) === String(controleFormNiveau))) return true;
            if (typeof p.niveau === 'object' && (String(p.niveau._id) === String(controleFormNiveau) || String(p.niveau.niveau_id) === String(controleFormNiveau))) return true;
            return false;
          })
      ).map((p) => (
        <MenuItem key={p._id} value={p._id}>
          {p.nom || p.intitule || p.libelle}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>
            
           <Grid item xs={12} md={4}>
  <FormControl fullWidth>
    <InputLabel id="module-label-form">Module</InputLabel>
    <Select labelId="module-label-form" value={controleFormModule} label="Module" onChange={(e) => { setControleFormModule(e.target.value); setControleForm({ ...controleForm, matiere_id: '' }); }}>
      <MenuItem value="">Sélectionner</MenuItem>
            {(() => {
        const aucuneContrainte = !controleFormAnnee && !controleFormFiliere && !controleFormNiveau && !controleForm.periode_id;
        if (aucuneContrainte) {
          return modules.map(m => (
            <MenuItem key={m._id} value={m._id}>
              {m.nom || m.code || m._id}
            </MenuItem>
          ));
        }

        // Résoudre l'ObjectId du formulaire vers le petit numéro attendu par les modules
        const filiereNum = (() => {
          const f = filieres.find(x =>
            memeId(x._id, controleFormFiliere) || memeId(x.filiere_id, controleFormFiliere)
          );
          return f ? String(f.filiere_id || f._id) : String(controleFormFiliere);
        })();

        const niveauNum = (() => {
          let n = niveaux.find(x =>
            memeId(x._id, controleFormNiveau) || memeId(x.niveau_id, controleFormNiveau)
          );
          if (!n) {
            for (const f of filieres) {
              if (Array.isArray(f.niveaux)) {
                const found = f.niveaux.find(x =>
                  memeId(x._id, controleFormNiveau) || memeId(x.niveau_id, controleFormNiveau)
                );
                if (found) { n = found; break; }
              }
            }
          }
          return n ? String(n.niveau_id || n._id) : String(controleFormNiveau);
        })();

        return modules
          .filter(m => {
            if (controleFormAnnee && m.annee_universitaire_id && !memeId(m.annee_universitaire_id, controleFormAnnee)) return false;
            if (filiereNum && m.filiere_id && !memeId(m.filiere_id, filiereNum)) return false;
            if (niveauNum && m.niveau_id && !memeId(m.niveau_id, niveauNum)) return false;
            if (controleForm.periode_id && m.periode_id && !memeId(m.periode_id, controleForm.periode_id)) return false;
            return true;
          })
          .map(m => (
            <MenuItem key={m._id} value={m._id}>
              {m.nom || m.code || m._id}
            </MenuItem>
          ));
      })()}
    </Select>
  </FormControl>
</Grid>
            <Grid item xs={12} md={4}>
  <FormControl fullWidth required>
    <InputLabel id="matiere-label-form">Matière *</InputLabel>
    <Select labelId="matiere-label-form" value={controleForm.matiere_id} label="Matière *" onChange={(e) => {
  const mid = e.target.value;
  const msel = matieres.find(x => memeId(x._id, mid));

  if (!msel) {
    setControleForm((prev) => ({ ...prev, matiere_id: mid }));
    return;
  }

  // Résoudre les petits numéros (filiere_id="93", niveau_id="32") vers les ObjectIds
  // utilisés par les Selects (car les Selects stockent l'_id).
  const resolveFiliereId = (petitNum) => {
    if (!petitNum) return undefined;
    const f = filieres.find(
      (x) => memeId(x.filiere_id, petitNum) || memeId(x._id, petitNum)
    );
    return f ? String(f._id) : String(petitNum);
  };

  const resolveNiveauId = (petitNum) => {
    if (!petitNum) return undefined;
    // Chercher dans la liste globale des niveaux
    let n = niveaux.find(
      (x) => memeId(x.niveau_id, petitNum) || memeId(x._id, petitNum)
    );
    // Sinon chercher dans filieres[i].niveaux
    if (!n) {
      for (const f of filieres) {
        if (Array.isArray(f.niveaux)) {
          const found = f.niveaux.find(
            (x) => memeId(x.niveau_id, petitNum) || memeId(x._id, petitNum)
          );
          if (found) { n = found; break; }
        }
      }
    }
    return n ? String(n._id) : String(petitNum);
  };

  const newFiliere = msel.filiere_id || msel.filiere
    ? resolveFiliereId(msel.filiere_id || msel.filiere)
    : undefined;
  const newNiveau = msel.niveau_id || msel.niveau
    ? resolveNiveauId(msel.niveau_id || msel.niveau)
    : undefined;
  const newModule = msel.module_id || msel.module || undefined;
  const newPeriode = msel.periode_id || msel.periode || undefined;
  const newAnnee = msel.annee_universitaire_id || msel.annee_id || undefined;

  // UN SEUL setState : pas d'écrasement, on garde les valeurs actuelles
  // si la matière ne porte pas le champ (au lieu de mettre undefined).
  setControleForm((prev) => ({
    ...prev,
    matiere_id: mid,
    annee_universitaire_id: newAnnee || prev.annee_universitaire_id,
    periode_id: newPeriode || prev.periode_id
  }));

  if (newModule) setControleFormModule(String(newModule));
  if (newNiveau) setControleFormNiveau(newNiveau);
  if (newFiliere) setControleFormFiliere(newFiliere);
}}>
      <MenuItem value="">Sélectionner</MenuItem>
            {(() => {
        const filiereNum = (() => {
          const f = filieres.find(x =>
            memeId(x._id, controleFormFiliere) || memeId(x.filiere_id, controleFormFiliere)
          );
          return f ? String(f.filiere_id || f._id) : String(controleFormFiliere);
        })();

        const niveauNum = (() => {
          let n = niveaux.find(x =>
            memeId(x._id, controleFormNiveau) || memeId(x.niveau_id, controleFormNiveau)
          );
          if (!n) {
            for (const f of filieres) {
              if (Array.isArray(f.niveaux)) {
                const found = f.niveaux.find(x =>
                  memeId(x._id, controleFormNiveau) || memeId(x.niveau_id, controleFormNiveau)
                );
                if (found) { n = found; break; }
              }
            }
          }
          return n ? String(n.niveau_id || n._id) : String(controleFormNiveau);
        })();

        return matieres
          .filter(m => {
            if (controleFormModule && (m.module_id || m.module) && !memeId(m.module_id || m.module, controleFormModule)) return false;
            if (controleFormAnnee && m.annee_universitaire_id && !memeId(m.annee_universitaire_id, controleFormAnnee)) return false;
            if (filiereNum && m.filiere_id && !memeId(m.filiere_id, filiereNum)) return false;
            if (niveauNum && m.niveau_id && !memeId(m.niveau_id, niveauNum)) return false;
            if (controleForm.periode_id && m.periode_id && !memeId(m.periode_id, controleForm.periode_id)) return false;
            return true;
          })
          .map(m => (
            <MenuItem key={m._id} value={m._id}>
              {m.nom || m.code} {m.code ? `(${m.code})` : ''}
            </MenuItem>
          ));
      })()}
    </Select>
  </FormControl>
</Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Coefficient" type="number" value={controleForm.coefficient} onChange={(e) => setControleForm({ ...controleForm, coefficient: Number(e.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Session</InputLabel>
                <Select value={controleForm.session} label="Session" onChange={(e) => setControleForm({ ...controleForm, session: e.target.value })}>
                  <MenuItem value="NORMALE">Normale</MenuItem>
                  <MenuItem value="RATTRAPAGE">Rattrapage</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Date du contrôle" type="date" InputLabelProps={{ shrink: true }} value={controleForm.date} onChange={(e) => setControleForm({ ...controleForm, date: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Heure début" type="time" InputLabelProps={{ shrink: true }} value={controleForm.controle_h_debut} onChange={(e) => setControleForm({ ...controleForm, controle_h_debut: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Heure fin" type="time" InputLabelProps={{ shrink: true }} value={controleForm.controle_h_fin} onChange={(e) => setControleForm({ ...controleForm, controle_h_fin: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeControleModal}>Annuler</Button>
          <Button onClick={saveControle} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL AJOUT/MODIFICATION NOTE ========== */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter une note' : '✏️ Modifier la note'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Étudiant *</InputLabel>
              <Select
                value={formData.etudiant_id}
                onChange={(e) => setFormData({ ...formData, etudiant_id: e.target.value })}
                label="Étudiant *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {etudiants.map((e) => (
                  <MenuItem key={e.etudiant_id} value={e.etudiant_id}>
                    {e.etudiant_nom} {e.etudiant_prenom} ({e.etudiant_cne_masar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Matière *</InputLabel>
              <Select
                value={formData.matiere_id}
                onChange={(e) => setFormData({ ...formData, matiere_id: e.target.value })}
                label="Matière *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {matieres.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.code} - {m.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Contrôle *</InputLabel>
              <Select
                value={formData.controle_id}
                onChange={(e) => setFormData({ ...formData, controle_id: e.target.value })}
                label="Contrôle *"
                required
              >
                <MenuItem value="">Sélectionner</MenuItem>
                {controles.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.intitule} (Coeff: {c.coefficient})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Note /20 *"
              type="number"
              inputProps={{ min: 0, max: 20, step: 0.25 }}
              value={formData.valeur}
              onChange={(e) => setFormData({ ...formData, valeur: parseFloat(e.target.value) || 0 })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Commentaire"
              value={formData.commentaire}
              onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button
            onClick={modalMode === 'add' ? handleAddNote : handleUpdateNote}
            variant="contained"
            sx={{ bgcolor: '#667eea' }}
          >
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notes;


